/**
 * Centralized logging hook
 * Logs to console and saves to JSON files via API
 */

import { useAction } from "@framework/shared/hooks/useAction";
import { useCallback } from "react";

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

export type LogEntry = {
  timestamp: number;
  level: LogLevel;
  message: string;
  resourceId?: string | number;
  payload?: any;
  source?: string;
};

type UseLogReturn = {
  log: (message: string, payload?: any, level?: LogLevel, source?: string) => void;
  logInfo: (message: string, payload?: any, source?: string) => void;
  logWarn: (message: string, payload?: any, source?: string) => void;
  logError: (message: string, payload?: any, source?: string) => void;
  logDebug: (message: string, payload?: any, source?: string) => void;
};

export function useLog(resourceId?: string | number): UseLogReturn {
  const log = useCallback((
    message: string, 
    payload?: any, 
    level: LogLevel = 'info',
    source?: string
  ) => {
    // Console logging with color coding
    const consoleMessage = `[${level.toUpperCase()}] ${message}`;
    switch (level) {
      case 'error':
        console.error(consoleMessage, payload || '');
        break;
      case 'warn':
        console.warn(consoleMessage, payload || '');
        break;
      case 'debug':
        console.debug(consoleMessage, payload || '');
        break;
      default:
        console.log(consoleMessage, payload || '');
    }
  }, []);

  const logInfo = useCallback((message: string, payload?: any, source?: string) => {
    log(message, payload, 'info', source);
  }, [log]);

  const logWarn = useCallback((message: string, payload?: any, source?: string) => {
    log(message, payload, 'warn', source);
  }, [log]);

  const logError = useCallback((message: string, payload?: any, source?: string) => {
    log(message, payload, 'error', source);
  }, [log]);

  const logDebug = useCallback((message: string, payload?: any, source?: string) => {
    log(message, payload, 'debug', source);
  }, [log]);

  return {
    log,
    logInfo,
    logWarn,
    logError,
    logDebug,
  };
}
